import React, { useState, useEffect, useCallback } from 'react';
import './AutoPilotModal.css';
import { getAutoPilotCandidates, startAutoPilot } from '../services/api';

const ARM_MULTIPLIER_OPTIONS = Array.from({ length: 21 }, (_, i) => (1 + i * 0.1).toFixed(1));

function fmt(n) {
  if (n === undefined || n === null || Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(n);
}

function emptyConfig(row) {
  return {
    included: true,
    maxLossRupees: row.maxLossDefault > 0 ? row.maxLossDefault : '',
    maxProfitRupees: '',
    minProfitRupees: row.minProfitDefault > 0 ? row.minProfitDefault : '',
    minProfitArmMultiplier: '1.25',
    scanIntervalMins: '',
    increaseQtyBy: '',
    reduceQtyBy: '',
    increaseQtyReps: '',
  };
}

function rowIsValid(cfg) {
  if (!cfg.included) return true;
  if (!(parseFloat(cfg.maxLossRupees) > 0)) return false;
  if (!(parseFloat(cfg.maxProfitRupees) > 0)) return false;
  if (!(parseFloat(cfg.minProfitRupees) > 0)) return false;
  if (!(parseFloat(cfg.minProfitArmMultiplier) >= 1)) return false;
  const scanInterval = parseFloat(cfg.scanIntervalMins) || 0;
  if (scanInterval > 0) {
    if (!(parseFloat(cfg.increaseQtyBy) > 0)) return false;
    if (!(parseFloat(cfg.reduceQtyBy) > 0)) return false;
    if (!(parseFloat(cfg.increaseQtyReps) > 0)) return false;
  }
  return true;
}

export default function AutoPilotModal({ onClose, onStarted }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [positions, setPositions] = useState([]);
  const [configs, setConfigs] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setConfirmed(false);
    try {
      const data = await getAutoPilotCandidates();
      const rows = data.positions || [];
      setPositions(rows);
      const cfgs = {};
      for (const row of rows) cfgs[row.symbol] = emptyConfig(row);
      setConfigs(cfgs);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load open positions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (symbol, field, value) => {
    setConfigs(prev => ({ ...prev, [symbol]: { ...prev[symbol], [field]: value } }));
  };

  const includedRows = positions.filter(p => configs[p.symbol]?.included);
  const allValid = includedRows.length > 0 && includedRows.every(p => rowIsValid(configs[p.symbol]));

  const handleSave = async () => {
    if (!allValid) return;
    setSaving(true);
    setSaveError('');
    try {
      const rows = includedRows.map(p => {
        const cfg = configs[p.symbol];
        return {
          symbol: p.symbol,
          direction: p.direction,
          quantity: p.quantity,
          avgPrice: p.avgPrice,
          maxLossRupees: parseFloat(cfg.maxLossRupees),
          maxProfitRupees: parseFloat(cfg.maxProfitRupees),
          minProfitRupees: parseFloat(cfg.minProfitRupees),
          minProfitArmMultiplier: parseFloat(cfg.minProfitArmMultiplier),
          scanIntervalMins: parseFloat(cfg.scanIntervalMins) || 0,
          increaseQtyBy: parseFloat(cfg.increaseQtyBy) || 0,
          reduceQtyBy: parseFloat(cfg.reduceQtyBy) || 0,
          increaseQtyReps: parseFloat(cfg.increaseQtyReps) || 0,
        };
      });
      await startAutoPilot(rows);
      onStarted();
    } catch (err) {
      setSaveError(err.response?.data?.error || err.message || 'Failed to start Auto Pilot');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="autopilot-modal-content" onClick={e => e.stopPropagation()}>
        <h2>Start Auto Pilot</h2>
        <p>
          Select which open positions Auto Pilot should manage. Once saved, every currently active algo
          is deactivated for the rest of the day — only Auto Pilot will place orders from then on.
        </p>

        {loading && <div className="ap-status">Loading open positions…</div>}
        {error && <div className="ap-status ap-error">{error}</div>}

        {!loading && !error && positions.length === 0 && (
          <div className="ap-status">No open positions found.</div>
        )}

        {!loading && !error && positions.length > 0 && (
          <div className="ap-grid-wrap">
            <table className="ap-grid">
              <thead>
                <tr>
                  <th>Include</th>
                  <th>Symbol</th>
                  <th>Qty</th>
                  <th>Avg Price</th>
                  <th>Amount</th>
                  <th>PnL</th>
                  <th>PnL%</th>
                  <th>Max Loss</th>
                  <th>Max Profit</th>
                  <th>Min Profit</th>
                  <th>Arm Mult.</th>
                  <th>Scan Interval (min)</th>
                  <th>Increase Qty By</th>
                  <th>Reduce Qty By</th>
                  <th>Increase Reps</th>
                </tr>
              </thead>
              <tbody>
                {positions.map(p => {
                  const cfg = configs[p.symbol] || emptyConfig(p);
                  const invalid = cfg.included && !rowIsValid(cfg);
                  return (
                    <tr key={p.symbol} className={!cfg.included ? 'ap-row-excluded' : invalid ? 'ap-row-invalid' : ''}>
                      <td>
                        <input type="checkbox" checked={cfg.included}
                          onChange={e => setField(p.symbol, 'included', e.target.checked)} />
                      </td>
                      <td className="ap-symbol">{p.symbol} <span className="ap-dir">{p.direction}</span></td>
                      <td>{fmt(p.quantity)}</td>
                      <td>{fmt(p.avgPrice)}</td>
                      <td>{fmt(p.amount)}</td>
                      <td className={p.pnl >= 0 ? 'ap-pos' : 'ap-neg'}>{fmt(p.pnl)}</td>
                      <td className={p.pnl >= 0 ? 'ap-pos' : 'ap-neg'}>{fmt(p.pnlPct)}%</td>
                      <td><input type="number" min="0" step="100" disabled={!cfg.included}
                        value={cfg.maxLossRupees} onChange={e => setField(p.symbol, 'maxLossRupees', e.target.value)} /></td>
                      <td><input type="number" min="0" step="100" disabled={!cfg.included}
                        value={cfg.maxProfitRupees} onChange={e => setField(p.symbol, 'maxProfitRupees', e.target.value)} /></td>
                      <td><input type="number" min="0" step="100" disabled={!cfg.included}
                        value={cfg.minProfitRupees} onChange={e => setField(p.symbol, 'minProfitRupees', e.target.value)} /></td>
                      <td>
                        <select disabled={!cfg.included} value={cfg.minProfitArmMultiplier}
                          onChange={e => setField(p.symbol, 'minProfitArmMultiplier', e.target.value)}>
                          {ARM_MULTIPLIER_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
                        </select>
                      </td>
                      <td><input type="number" min="0" step="1" disabled={!cfg.included}
                        value={cfg.scanIntervalMins} onChange={e => setField(p.symbol, 'scanIntervalMins', e.target.value)} /></td>
                      <td><input type="number" min="0" step="1" disabled={!cfg.included}
                        value={cfg.increaseQtyBy} onChange={e => setField(p.symbol, 'increaseQtyBy', e.target.value)} /></td>
                      <td><input type="number" min="0" step="1" disabled={!cfg.included}
                        value={cfg.reduceQtyBy} onChange={e => setField(p.symbol, 'reduceQtyBy', e.target.value)} /></td>
                      <td><input type="number" min="0" step="1" disabled={!cfg.included}
                        value={cfg.increaseQtyReps} onChange={e => setField(p.symbol, 'increaseQtyReps', e.target.value)} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {saveError && <div className="ap-status ap-error">{saveError}</div>}

        {!loading && !error && positions.length > 0 && (
          <label className="ap-confirm-row">
            <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
            I understand this will deactivate every other active algo for the rest of today.
          </label>
        )}

        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving || !allValid || !confirmed}>
            {saving ? 'Starting…' : 'Save & Start Auto Pilot'}
          </button>
        </div>
      </div>
    </div>
  );
}
